import React, {useState, useEffect} from 'react';

import { faArrowLeft, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Box, CircularProgress, CardActionArea } from '@mui/material';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useSelector, useDispatch } from 'react-redux';
import { useMap } from 'react-map-gl';
import { useTranslation } from 'react-i18next';

import { setContentType, setOpenSideBar } from '../store/actions/sideBarActions';
import { selectFeature, unselectFeature } from '../store/actions/selectedFeatureActions';
import { CardRoadStep } from '../components/Card/CardRoadStep';
import { getBounds } from '../utils/useBounds';
import { PointWithLabelRoad } from '../components/PointWithLabelRoad';
import { TransportVisual } from '../components/TransportVisual';
import { PedestrianVisual } from '../components/PedestrianVisual';

export const Roadmap = ({typeAcc, status, allResults, selectedPath, isMobile}) => {
  const {t} = useTranslation();
  const { mymap } = useMap();

  const [selectedFeature, setSelectedFeature] = useState();
  const dispatch = useDispatch();
  const origin = useSelector(state => state.directions.origin);
  const dest = useSelector(state => state.directions.destination);
  const goBack = async() => {
   
    dispatch(unselectFeature());
    dispatch(setOpenSideBar(false));
    // add a timout for mobile mode to avoid strange transition
    if (isMobile) {
      await new Promise(res => setTimeout(res, 50));
    }
    dispatch(setContentType('direction'));
  };
  let features = [];
  let timeDepart = undefined;
  let timeDest = undefined;

  if (status === 'success' && allResults.length > 0) {
    if (selectedPath > allResults.length - 1) {
      features = allResults[0].geojson.features;
      if (allResults[0] && allResults[0].locations) {
        const hourDepart = allResults[0].locations[0].date_time;
        const hourDest = allResults[0].locations[1].date_time;
        if (hourDepart && hourDest) {
          timeDepart = hourDepart.slice(hourDepart.indexOf('T') + 1);
          timeDest = hourDest.slice(hourDest.indexOf('T') + 1);
        }
      }
    } else {
      features = allResults[selectedPath].geojson.features;
      if (allResults[selectedPath] && allResults[selectedPath].locations) {
        const hourDepart = allResults[selectedPath].locations[0].date_time;
        const hourDest = allResults[selectedPath].locations[1].date_time;
        if (hourDepart && hourDest) {
          timeDepart = hourDepart.slice(hourDepart.indexOf('T') + 1);
          timeDest = hourDest.slice(hourDest.indexOf('T') + 1);
        }
      }
    }
  }

  const hoverAndFocusCardCallBack = (featureID) => {
    dispatch(selectFeature(features[featureID], 'selectedpath_line'));
    setSelectedFeature(featureID);
  };

  const clickCardCallBack = (featureID) => {
    dispatch(selectFeature(features[featureID],'selectedpath_line' ,'stepsDetails'));
    setSelectedFeature(featureID);
    dispatch(setContentType('selectedPathDetails'));
  };

  const flyToPath = (featureID) => {
    const featurePath = {
      type: 'FeatureCollection',
      features: [features[featureID]]
    };
    const bounds = getBounds(featurePath);
    if (bounds !== undefined) {
      setTimeout(() => {
        mymap?.fitBounds(bounds, {
          padding: {
            top: isMobile ? 100 : 50,
            bottom: isMobile ? 100 : 20,
            left: isMobile ? 50: 25,
            right: isMobile ? 50: 25
          }
        });
      }, 100);
    }
  };

  const renderVisualPath = (actualFeature, nextFeature) => {
    const {properties} = actualFeature;
    switch (properties.m) {
      case 'transit':
        return (
          <TransportVisual properties={properties} />
        );
      default:
        return (
          <PedestrianVisual 
            typeAcc={typeAcc} 
            actualFeature={actualFeature} 
            nextFeature={nextFeature}  
          />
        );
    }
  };

  useEffect(() => {
    dispatch(unselectFeature());
    setSelectedFeature(null);
  }, [status]);

  return (
    <>
      <Box sx={{
        display: 'flex', 
        paddingX: '1em',
        backgroundColor: 'inherit',
        alignItems: 'center',  
        position:'sticky',
        top:0,
        paddingTop: '1em',
        zIndex:100
      }}>
        <IconButton onClick={goBack} >
          <FontAwesomeIcon style={{paddingRight: '.5em'}} color='white' icon={faArrowLeft} />
          <Typography sx={{ fontSize: '1em', color: 'white', marginLeft: '10px' }}>
            {t('common.return-side-bar')}
          </Typography>
        </IconButton>
      </Box>
      <Box sx={{display: 'flex', justifyContent: 'center'}}>
        <Typography sx={{ color: 'white', fontSize: '1.5rem' }} variant='h1' gutterBottom>
          {t('stepPath.page-label')}
        </Typography>
      </Box>
      {status === 'success' &&
      <Box display='flex' flexDirection='column' backgroundColor='#F8F9FA'>
        <List
          sx={{
            width: '100%',
            bgcolor: 'background.paper'
          }}
        >
          <ListItem>
            <PointWithLabelRoad label={origin.label} time={timeDepart}>
              <FontAwesomeIcon id='origin-marker-icon' icon={faMapMarkerAlt} color='green' style= {{fontSize: '1.6em'}}/>
            </PointWithLabelRoad>
          </ListItem>
          {features.map((feature, i) => (
            <ListItem key={i} sx={{display:'flex', flexDirection:'column'}}>
              <CardRoadStep key={i} active={i === selectedFeature ? 'true' : 'false'}>
                <CardActionArea 
                  onMouseEnter={() => hoverAndFocusCardCallBack(i)} 
                  onFocus={() => hoverAndFocusCardCallBack(i)} 
                  onClick={() => {
                    clickCardCallBack(i);
                    flyToPath(i);
                  }}
                >
                  {renderVisualPath(feature, features[i+1])}
                </CardActionArea>
              </CardRoadStep>
            </ListItem>
          ))}
          <ListItem>
            <PointWithLabelRoad label={dest.label} time={timeDest}>
              <FontAwesomeIcon id='origin-marker-icon' icon={faMapMarkerAlt} color='blue' style= {{fontSize: '1.6em'}}/>
            </PointWithLabelRoad>
          </ListItem>
        </List> 
      </Box>
      }
      {status === 'loading' && 
    (
      <Box display='flex' sx={{width: '100%', justifyContent: 'center', paddingY: '1em', height: '100%'}} backgroundColor='#F8F9FA'>
        <CircularProgress  />
      </Box>
    )
      }
    </>
  );
};

